import {
  DPIA_CASE_STATUSES,
  DPIA_DECISION_OPTIONS,
  DPIA_IMPACT_OPTIONS,
  DPIA_LIKELIHOOD_OPTIONS,
  DPIA_MEASURE_STATUS_OPTIONS,
  DPIA_PRIOR_CONSULTATION_OPTIONS,
  DPIA_RESIDUAL_RISK_OPTIONS,
  DPIA_RISK_LEVEL_OPTIONS,
} from './dpias.constants';
import type {
  DpiaClarificationHistoryAuthorRole,
  DpiaClarificationHistoryEntry,
  DpiaContext,
  DpiaContextDTO,
  DpiaDecision,
  DpiaDetail,
  DpiaDetailDTO,
  DpiaDetailResponse,
  DpiaImpact,
  DpiaLikelihood,
  DpiaMeasure,
  DpiaMeasureDTO,
  DpiaMeasureStatus,
  DpiaPriorConsultationAssessment,
  DpiaResidualRiskLevel,
  CreateDpiaRiskInput,
  DpiaRisk,
  DpiaRiskDTO,
  DpiaRiskLevel,
  DpiaStatus,
  UpdateDpiaInput,
} from './dpias.types';

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const readString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const readNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

const readNullableNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const readNullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const readBoolean = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const includesOption = <T extends string>(options: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (options as readonly string[]).includes(value);

const parseDpiaStatus = (value: unknown): DpiaStatus => {
  if (includesOption(DPIA_CASE_STATUSES, value)) {
    return value;
  }

  return 'draft';
};

const parseDpiaDecision = (value: unknown): DpiaDecision | null => (includesOption(DPIA_DECISION_OPTIONS, value) ? value : null);

const parseResidualRiskLevel = (value: unknown): DpiaResidualRiskLevel =>
  includesOption(DPIA_RESIDUAL_RISK_OPTIONS, value) ? value : 'not_assessed';

const parsePriorConsultation = (value: unknown): DpiaPriorConsultationAssessment =>
  includesOption(DPIA_PRIOR_CONSULTATION_OPTIONS, value) ? value : 'not_assessed';

const parseLikelihood = (value: unknown): DpiaLikelihood | '' => (includesOption(DPIA_LIKELIHOOD_OPTIONS, value) ? value : '');

const parseImpact = (value: unknown): DpiaImpact | '' => (includesOption(DPIA_IMPACT_OPTIONS, value) ? value : '');

const parseRiskLevel = (value: unknown): DpiaRiskLevel | '' => (includesOption(DPIA_RISK_LEVEL_OPTIONS, value) ? value : '');

const parseMeasureStatus = (value: unknown): DpiaMeasureStatus => (includesOption(DPIA_MEASURE_STATUS_OPTIONS, value) ? value : 'planned');

export const parseMeasureForFE = (dto: DpiaMeasureDTO): DpiaMeasure => ({
  id: dto.id,
  dpiaRiskId: dto.dpia_risk_id,
  title: dto.title,
  description: dto.description,
  responsibleMembershipId: dto.responsible_membership_id,
  responsibleName: dto.responsible_name,
  dueDate: dto.due_date,
  status: dto.status,
  sortOrder: dto.sort_order,
});

const parseMeasureRecord = (value: unknown): DpiaMeasure | null => {
  if (!isRecord(value) || typeof value.id !== 'number') {
    return null;
  }

  return parseMeasureForFE({
    id: value.id,
    dpia_risk_id: readNullableNumber(value.dpia_risk_id),
    title: readString(value.title),
    description: readString(value.description),
    responsible_membership_id: readNumber(value.responsible_membership_id),
    responsible_name: readString(value.responsible_name),
    due_date: readNullableString(value.due_date),
    status: parseMeasureStatus(value.status),
    sort_order: readNumber(value.sort_order),
  });
};

export const parseRiskForFE = (dto: DpiaRiskDTO): DpiaRisk => ({
  id: dto.id,
  title: dto.title,
  description: dto.description,
  affectedRightsAndFreedoms: dto.affected_rights_and_freedoms,
  likelihood: dto.likelihood,
  impact: dto.impact,
  riskLevel: dto.risk_level,
  measures: (dto.measures ?? []).map(parseMeasureForFE),
  sortOrder: dto.sort_order,
});

const parseRiskRecord = (value: unknown): DpiaRisk | null => {
  if (!isRecord(value) || typeof value.id !== 'number') {
    return null;
  }

  const nestedMeasures = Array.isArray(value.measures)
    ? value.measures.map(parseMeasureRecord).filter((measure): measure is DpiaMeasure => measure !== null)
    : [];

  return {
    id: value.id,
    title: readString(value.title),
    description: readString(value.description),
    affectedRightsAndFreedoms: readString(value.affected_rights_and_freedoms),
    likelihood: parseLikelihood(value.likelihood),
    impact: parseImpact(value.impact),
    riskLevel: parseRiskLevel(value.risk_level),
    measures: nestedMeasures,
    sortOrder: readNumber(value.sort_order),
  };
};

type LegacyClarificationHistoryFields = {
  administratorClarificationComment: string;
  clarificationRequestedAt: string | null;
  clarificationRequestedByName: string | null;
  ownerResponseComment: string;
  responsibleName: string;
  submittedAt: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
};

const parseClarificationHistoryAuthorRole = (value: unknown): DpiaClarificationHistoryAuthorRole =>
  value === 'owner' ? 'owner' : 'administrator';

export const parseDpiaClarificationHistoryEntryForFE = (dto: unknown): DpiaClarificationHistoryEntry | null => {
  if (!isRecord(dto)) {
    return null;
  }

  const comment = readString(dto.comment).trim();
  if (!comment) {
    return null;
  }

  return {
    id: readNumber(dto.id, -1),
    comment,
    createdAt: readString(dto.created_at),
    authorName: readString(dto.author_name, '—'),
    authorRole: parseClarificationHistoryAuthorRole(dto.author_role),
  };
};

export const buildLegacyClarificationHistory = (fields: LegacyClarificationHistoryFields): DpiaClarificationHistoryEntry[] => {
  const entries: DpiaClarificationHistoryEntry[] = [];

  if (fields.administratorClarificationComment.trim()) {
    entries.push({
      id: -1,
      comment: fields.administratorClarificationComment.trim(),
      createdAt: fields.clarificationRequestedAt ?? fields.updatedAt ?? '',
      authorName: fields.clarificationRequestedByName?.trim() || '—',
      authorRole: 'administrator',
    });
  }

  if (fields.ownerResponseComment.trim()) {
    entries.push({
      id: -2,
      comment: fields.ownerResponseComment.trim(),
      createdAt: fields.updatedAt ?? fields.submittedAt ?? '',
      authorName: fields.updatedByName?.trim() || fields.responsibleName || '—',
      authorRole: 'owner',
    });
  }

  return entries;
};

const resolveClarificationHistory = (
  clarificationHistory: unknown,
  legacyFields: LegacyClarificationHistoryFields,
): DpiaClarificationHistoryEntry[] => {
  const parsedHistory = Array.isArray(clarificationHistory)
    ? clarificationHistory
        .map(parseDpiaClarificationHistoryEntryForFE)
        .filter((entry): entry is DpiaClarificationHistoryEntry => entry !== null)
    : [];

  if (parsedHistory.length > 0) {
    return parsedHistory;
  }

  return buildLegacyClarificationHistory(legacyFields);
};

export const parseDpiaDetailForFE = (dto: DpiaDetailDTO): DpiaDetail => ({
  id: dto.id,
  organizationId: dto.organization_id,
  assetProcessingActivityId: dto.asset_processing_activity_id,
  sourceDpiaRequestId: dto.source_dpia_request_id,
  title: dto.title,
  status: parseDpiaStatus(dto.status),
  responsibleMembershipId: dto.responsible_membership_id,
  responsibleName: dto.responsible_name,
  initiatedAt: dto.initiated_at,
  plannedProcessingDescription: dto.planned_processing_description,
  assessmentScope: dto.assessment_scope,
  projectOrChangeContext: dto.project_or_change_context,
  selectedPurposeIds: dto.selected_purpose_ids,
  necessityAssessment: dto.necessity_assessment,
  proportionalityAssessment: dto.proportionality_assessment,
  dataMinimisationAssessment: dto.data_minimisation_assessment,
  retentionAndAccessAssessment: dto.retention_and_access_assessment,
  overallAssessment: dto.overall_assessment,
  residualRiskLevel: dto.residual_risk_level,
  priorConsultationAssessment: dto.prior_consultation_assessment,
  ownerConclusion: dto.owner_conclusion,
  administratorClarificationComment: dto.administrator_clarification_comment,
  ownerResponseComment: dto.owner_response_comment,
  revision: dto.revision,
  submittedRevision: dto.submitted_revision,
  submittedAt: dto.submitted_at,
  submittedByMembershipId: dto.submitted_by_membership_id,
  submittedByName: dto.submitted_by_name,
  updatedAt: dto.updated_at,
  updatedByMembershipId: dto.updated_by_membership_id,
  updatedByName: dto.updated_by_name,
  reviewDecisionComment: dto.review_decision_comment,
  decision: parseDpiaDecision(dto.decision),
  approvedAt: dto.approved_at,
  approvedByMembershipId: dto.approved_by_membership_id,
  approvedByName: dto.approved_by_name,
  approvedRevision: dto.approved_revision,
  clarificationRequestedAt: dto.clarification_requested_at,
  clarificationRequestedByMembershipId: dto.clarification_requested_by_membership_id,
  clarificationRequestedByName: dto.clarification_requested_by_name,
  clarificationHistory: resolveClarificationHistory(dto.clarification_history, {
    administratorClarificationComment: dto.administrator_clarification_comment,
    clarificationRequestedAt: dto.clarification_requested_at,
    clarificationRequestedByName: dto.clarification_requested_by_name,
    ownerResponseComment: dto.owner_response_comment,
    responsibleName: dto.responsible_name,
    submittedAt: dto.submitted_at,
    updatedAt: dto.updated_at,
    updatedByName: dto.updated_by_name,
  }),
  risks: (dto.risks ?? []).map(parseRiskForFE),
  canEdit: dto.can_edit,
  changedAfterSubmission: Boolean(dto.changed_after_submission),
});

const parseSourceRequest = (value: unknown): DpiaContextDTO['source_request'] => {
  const record = isRecord(value) ? value : {};

  return {
    recommendation: readString(record.recommendation),
    decision: readString(record.decision),
    decision_comment: readString(record.decision_comment),
    trigger_source: typeof record.trigger_source === 'string' ? record.trigger_source : null,
    trigger_comment: typeof record.trigger_comment === 'string' ? record.trigger_comment : '',
  };
};

type PurposeTextEnrichment = {
  name: string;
  legalBasis: string;
  applicationDescription: string;
};

const buildPurposeTextLookup = (assetRopaContext: unknown): Map<number, PurposeTextEnrichment> => {
  const record = isRecord(assetRopaContext) ? assetRopaContext : {};
  const processingActivity = isRecord(record.processing_activity) ? record.processing_activity : {};
  const processingPurposes = Array.isArray(processingActivity.purposes) ? processingActivity.purposes : [];

  const bySourcePurposeId = new Map<number, PurposeTextEnrichment>();
  processingPurposes.forEach(item => {
    if (!isRecord(item) || typeof item.id !== 'number') {
      return;
    }

    bySourcePurposeId.set(item.id, {
      name: readString(item.purpose_text, readString(item.name)),
      legalBasis: readString(item.legal_basis),
      applicationDescription: readString(item.application_description, readString(item.description)),
    });
  });

  const byAssetProcessingPurposeId = new Map<number, PurposeTextEnrichment>();
  const ropaPurposes = Array.isArray(record.purposes) ? record.purposes : [];
  ropaPurposes.forEach(item => {
    if (!isRecord(item) || typeof item.id !== 'number') {
      return;
    }

    const sourcePurposeId = readNumber(item.source_processing_purpose_id);
    const enrichment = bySourcePurposeId.get(sourcePurposeId);
    if (enrichment) {
      byAssetProcessingPurposeId.set(item.id, enrichment);
    }
  });

  return byAssetProcessingPurposeId;
};

const isPurposeScopeSelected = (item: Record<string, unknown>): boolean => {
  if (item.selected === false || item.is_selected === false) {
    return false;
  }

  const applicabilityStatus = readString(item.applicability_status);
  if (applicabilityStatus) {
    return applicabilityStatus === 'applies';
  }

  return true;
};

const parsePurposeScopeItem = (
  value: unknown,
  purposeTextByAssetPurposeId: Map<number, PurposeTextEnrichment> = new Map(),
): DpiaContext['availablePurposes'][number] | null => {
  if (!isRecord(value) || typeof value.id !== 'number') {
    return null;
  }

  const assetProcessingPurposeId = readNumber(value.asset_processing_purpose_id, value.id);
  const enrichment = purposeTextByAssetPurposeId.get(assetProcessingPurposeId);

  return {
    id: value.id,
    assetProcessingPurposeId,
    name: readString(value.name) || enrichment?.name || '',
    legalBasis: readString(value.legal_basis) || enrichment?.legalBasis || '',
    applicationDescription: readString(value.application_description) || enrichment?.applicationDescription || '',
  };
};

const parsePurposeScopes = (
  value: unknown,
  purposeTextByAssetPurposeId: Map<number, PurposeTextEnrichment> = new Map(),
): { ids: number[]; purposes: DpiaContext['availablePurposes'] } => {
  if (!Array.isArray(value)) {
    return { ids: [], purposes: [] };
  }

  const purposes = value
    .map(item => parsePurposeScopeItem(item, purposeTextByAssetPurposeId))
    .filter((item): item is DpiaContext['availablePurposes'][number] => item !== null);
  const selectedIds = value
    .map(item => {
      if (!isRecord(item) || typeof item.id !== 'number' || !isPurposeScopeSelected(item)) {
        return null;
      }

      return item.id;
    })
    .filter((id): id is number => id !== null);

  return { ids: selectedIds, purposes };
};

const readPersonName = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.name === 'string' && value.name.trim()) {
    return value.name;
  }

  const profile = isRecord(value.profile) ? value.profile : {};
  const fullName = `${readString(profile.first_name)} ${readString(profile.last_name)}`.trim();
  return fullName || null;
};

const readCategoryNameList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }

      if (isRecord(item) && typeof item.name === 'string') {
        return item.name;
      }

      return '';
    })
    .filter((item): item is string => item.length > 0);
};

const readCountryHandlingList = (record: Record<string, unknown>, processingActivity: Record<string, unknown>): string[] => {
  const fromFlat = readStringArray(record.country_handling);
  if (fromFlat.length > 0) {
    return fromFlat;
  }

  const countrySummary = isRecord(record.country_summary) ? record.country_summary : {};
  const fromSummary = Array.isArray(countrySummary.country_codes)
    ? countrySummary.country_codes.filter((item): item is string => typeof item === 'string')
    : [];
  if (fromSummary.length > 0) {
    return fromSummary;
  }

  const fromNested = readStringArray(processingActivity.country_handling);
  if (fromNested.length > 0) {
    return fromNested;
  }

  return readCategoryNameList(processingActivity.countries);
};

const normalizeAssetRopaContextDto = (
  record: Record<string, unknown>,
  purposeTextLookup: Map<number, PurposeTextEnrichment>,
): DpiaContextDTO => {
  const processingActivity = isRecord(record.processing_activity) ? record.processing_activity : {};
  const asset = isRecord(record.asset) ? record.asset : {};
  let assetType: Record<string, unknown> = {};
  if (isRecord(record.asset_type)) {
    assetType = record.asset_type;
  } else if (isRecord(asset.asset_type)) {
    assetType = asset.asset_type;
  }
  const dataSubjectCategories = readCategoryNameList(record.data_subject_categories);
  const personalDataCategories = readCategoryNameList(record.personal_data_categories);

  return {
    asset_name: readString(record.asset_name) || readString(asset.name) || readString(processingActivity.asset_name),
    asset_type_name: readString(record.asset_type_name) || readString(assetType.name) || readString(asset.type_name),
    asset_processing_activity_name:
      readString(record.asset_processing_activity_name) ||
      readString(record.processing_activity_name) ||
      readString(processingActivity.name),
    operational_owner_name:
      readNullableString(record.operational_owner_name) ??
      readPersonName(record.operational_owner) ??
      readPersonName(processingActivity.operational_owner) ??
      readPersonName(asset.operational_owner),
    data_subject_categories:
      dataSubjectCategories.length > 0 ? dataSubjectCategories : readCategoryNameList(processingActivity.data_subject_categories),
    personal_data_categories:
      personalDataCategories.length > 0 ? personalDataCategories : readCategoryNameList(processingActivity.personal_data_categories),
    country_handling: readCountryHandlingList(record, processingActivity),
    minimum_security_assessment:
      readString(record.minimum_security_assessment) ||
      readString(processingActivity.minimum_security_assessment) ||
      readString(processingActivity.minimum_security_measures),
    available_purposes: Array.isArray(record.available_purposes)
      ? record.available_purposes.flatMap(item => {
          const parsedPurpose = parsePurposeScopeItem(item, purposeTextLookup);
          return parsedPurpose
            ? [
                {
                  id: parsedPurpose.id,
                  asset_processing_purpose_id: parsedPurpose.assetProcessingPurposeId,
                  name: parsedPurpose.name,
                  legal_basis: parsedPurpose.legalBasis,
                  application_description: parsedPurpose.applicationDescription,
                },
              ]
            : [];
        })
      : [],
    source_request: parseSourceRequest(record.source_request),
  };
};

export const parseDpiaContextForFE = (dto: DpiaContextDTO): DpiaContext => ({
  assetName: dto.asset_name,
  assetTypeName: dto.asset_type_name,
  assetProcessingActivityName: dto.asset_processing_activity_name,
  operationalOwnerName: dto.operational_owner_name,
  dataSubjectCategories: dto.data_subject_categories ?? [],
  personalDataCategories: dto.personal_data_categories ?? [],
  countryHandling: dto.country_handling ?? [],
  minimumSecurityAssessment: dto.minimum_security_assessment,
  availablePurposes: (dto.available_purposes ?? []).map(purpose => ({
    id: purpose.id,
    assetProcessingPurposeId: purpose.asset_processing_purpose_id,
    name: purpose.name,
    legalBasis: purpose.legal_basis,
    applicationDescription: purpose.application_description,
  })),
  sourceRequest: {
    recommendation: dto.source_request.recommendation,
    decision: dto.source_request.decision,
    decisionComment: dto.source_request.decision_comment,
    triggerSource: typeof dto.source_request.trigger_source === 'string' ? dto.source_request.trigger_source : null,
    triggerComment: typeof dto.source_request.trigger_comment === 'string' ? dto.source_request.trigger_comment : '',
  },
});

const parseContextRecord = (value: unknown, fallbackPurposes: DpiaContext['availablePurposes'] = []): DpiaContext => {
  const record = isRecord(value) ? value : {};
  const purposeTextLookup = buildPurposeTextLookup(record);
  const parsed = parseDpiaContextForFE(normalizeAssetRopaContextDto(record, purposeTextLookup));

  if (parsed.availablePurposes.length === 0 && fallbackPurposes.length > 0) {
    return { ...parsed, availablePurposes: fallbackPurposes };
  }

  return parsed;
};

const isEnvelopeDto = (value: unknown): value is { dpia: DpiaDetailDTO; context: DpiaContextDTO } =>
  isRecord(value) && isRecord(value.dpia) && typeof value.dpia.id === 'number' && isRecord(value.context);

const isEditorOutputDto = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && typeof value.id === 'number' && !isRecord(value.dpia);

const isDpiaEditorOutputDto = (value: Record<string, unknown>): boolean =>
  Array.isArray(value.risks) ||
  Array.isArray(value.measures) ||
  Array.isArray(value.purpose_scopes) ||
  isRecord(value.asset_ropa_context) ||
  typeof value.is_editable === 'boolean' ||
  (typeof value.status === 'string' && typeof value.revision === 'number' && typeof value.planned_processing_description === 'string');

const isSingleRiskDto = (value: Record<string, unknown>): boolean => typeof value.id === 'number' && !isDpiaEditorOutputDto(value);

type ParseRiskMutationResponseOptions = {
  riskId?: number;
  payload?: Pick<CreateDpiaRiskInput, 'title' | 'sortOrder'>;
  knownRiskIds?: number[];
};

const parseEditorOutputForFE = (dto: Record<string, unknown>): DpiaDetailResponse => {
  const assetRopaContext = dto.asset_ropa_context ?? dto.context;
  const purposeTextLookup = buildPurposeTextLookup(assetRopaContext);
  const purposeScopes = parsePurposeScopes(dto.purpose_scopes, purposeTextLookup);
  const looseMeasures = Array.isArray(dto.measures)
    ? dto.measures.map(parseMeasureRecord).filter((measure): measure is DpiaMeasure => measure !== null)
    : [];
  const measuresByRiskId = looseMeasures.reduce<Record<number, DpiaMeasure[]>>((accumulator, measure) => {
    if (measure.dpiaRiskId == null) {
      return accumulator;
    }

    const current = accumulator[measure.dpiaRiskId] ?? [];
    return { ...accumulator, [measure.dpiaRiskId]: [...current, measure] };
  }, {});

  const risks = Array.isArray(dto.risks)
    ? dto.risks
        .map(risk => {
          const parsed = parseRiskRecord(risk);
          if (!parsed) {
            return null;
          }

          if (parsed.measures.length > 0) {
            return parsed;
          }

          return { ...parsed, measures: measuresByRiskId[parsed.id] ?? [] };
        })
        .filter((risk): risk is DpiaRisk => risk !== null)
    : [];

  const selectedPurposeIds = Array.isArray(dto.selected_purpose_ids)
    ? dto.selected_purpose_ids.filter((id): id is number => typeof id === 'number')
    : purposeScopes.ids;
  const assetRopaContextId = isRecord(assetRopaContext) ? readNumber(assetRopaContext.id) : 0;
  const assetProcessingActivityId = readNumber(dto.asset_processing_activity_id) || assetRopaContextId;

  return {
    dpia: {
      id: dto.id as number,
      organizationId: readNumber(dto.organization_id),
      assetProcessingActivityId,
      sourceDpiaRequestId: readNullableNumber(dto.source_dpia_request_id),
      title: readString(dto.title),
      status: parseDpiaStatus(dto.status),
      responsibleMembershipId: readNumber(dto.responsible_membership_id),
      responsibleName: readString(dto.responsible_name),
      initiatedAt: readString(dto.initiated_at),
      plannedProcessingDescription: readString(dto.planned_processing_description),
      assessmentScope: readString(dto.assessment_scope),
      projectOrChangeContext: readString(dto.project_or_change_context),
      selectedPurposeIds,
      necessityAssessment: readString(dto.necessity_assessment),
      proportionalityAssessment: readString(dto.proportionality_assessment),
      dataMinimisationAssessment: readString(dto.data_minimisation_assessment),
      retentionAndAccessAssessment: readString(dto.retention_and_access_assessment),
      overallAssessment: readString(dto.overall_assessment),
      residualRiskLevel: parseResidualRiskLevel(dto.residual_risk_level),
      priorConsultationAssessment: parsePriorConsultation(dto.prior_consultation_assessment),
      ownerConclusion: readString(dto.owner_conclusion),
      administratorClarificationComment: readString(dto.administrator_clarification_comment),
      ownerResponseComment: readString(dto.owner_response_comment),
      revision: readNumber(dto.revision),
      submittedRevision: readNullableNumber(dto.submitted_revision),
      submittedAt: readNullableString(dto.submitted_at),
      submittedByMembershipId: readNullableNumber(dto.submitted_by_membership_id),
      submittedByName: readNullableString(dto.submitted_by_name),
      updatedAt: readNullableString(dto.updated_at),
      updatedByMembershipId: readNullableNumber(dto.updated_by_membership_id),
      updatedByName: readNullableString(dto.updated_by_name),
      reviewDecisionComment: readString(dto.review_decision_comment),
      decision: parseDpiaDecision(dto.decision),
      approvedAt: readNullableString(dto.approved_at),
      approvedByMembershipId: readNullableNumber(dto.approved_by_membership_id),
      approvedByName: readNullableString(dto.approved_by_name),
      approvedRevision: readNullableNumber(dto.approved_revision),
      clarificationRequestedAt: readNullableString(dto.clarification_requested_at),
      clarificationRequestedByMembershipId: readNullableNumber(dto.clarification_requested_by_membership_id),
      clarificationRequestedByName: readNullableString(dto.clarification_requested_by_name),
      clarificationHistory: resolveClarificationHistory(dto.clarification_history, {
        administratorClarificationComment: readString(dto.administrator_clarification_comment),
        clarificationRequestedAt: readNullableString(dto.clarification_requested_at),
        clarificationRequestedByName: readNullableString(dto.clarification_requested_by_name),
        ownerResponseComment: readString(dto.owner_response_comment),
        responsibleName: readString(dto.responsible_name),
        submittedAt: readNullableString(dto.submitted_at),
        updatedAt: readNullableString(dto.updated_at),
        updatedByName: readNullableString(dto.updated_by_name),
      }),
      risks,
      canEdit: readBoolean(dto.can_edit, readBoolean(dto.is_editable)),
      changedAfterSubmission: readBoolean(dto.changed_after_submission),
    },
    context: parseContextRecord(assetRopaContext, purposeScopes.purposes),
  };
};

export const parseRiskMutationResponseForFE = (data: unknown, options: ParseRiskMutationResponseOptions = {}): DpiaRisk => {
  if (isRecord(data) && isSingleRiskDto(data)) {
    const parsed = parseRiskRecord(data);
    if (parsed) {
      return parsed;
    }
  }

  if (isEditorOutputDto(data) && isDpiaEditorOutputDto(data)) {
    const { dpia } = parseEditorOutputForFE(data);

    if (options.riskId !== undefined) {
      const updatedRisk = dpia.risks.find(risk => risk.id === options.riskId);
      if (updatedRisk) {
        return updatedRisk;
      }
    }

    const knownRiskIds = options.knownRiskIds ?? [];
    const createdCandidates = dpia.risks.filter(risk => !knownRiskIds.includes(risk.id));

    if (createdCandidates.length === 1) {
      return createdCandidates[0];
    }

    if (options.payload?.title) {
      const normalizedTitle = options.payload.title.trim();
      const matchedRisk = createdCandidates.find(risk => risk.title.trim() === normalizedTitle);
      if (matchedRisk) {
        return matchedRisk;
      }
    }

    if (options.payload?.sortOrder !== undefined) {
      const matchedRisk = createdCandidates.find(risk => risk.sortOrder === options.payload?.sortOrder);
      if (matchedRisk) {
        return matchedRisk;
      }
    }

    const fallbackRisk = createdCandidates[createdCandidates.length - 1];
    if (fallbackRisk) {
      return fallbackRisk;
    }
  }

  throw new Error('DPIA risk response could not be parsed.');
};

export const readDpiaRevisionFromResponse = (data: unknown): number | null => {
  if (!isRecord(data)) {
    return null;
  }

  if (typeof data.revision === 'number') {
    return data.revision;
  }

  if (isRecord(data.dpia) && typeof data.dpia.revision === 'number') {
    return data.dpia.revision;
  }

  return null;
};

export const parseDpiaDetailResponseForFE = (dto: unknown): DpiaDetailResponse => {
  if (isEnvelopeDto(dto)) {
    return {
      dpia: parseDpiaDetailForFE(dto.dpia),
      context: parseDpiaContextForFE(dto.context),
    };
  }

  if (isEditorOutputDto(dto)) {
    return parseEditorOutputForFE(dto);
  }

  throw new Error('DPIA detail response could not be parsed.');
};

export const parseMeasureForBE = (measure: Partial<Omit<DpiaMeasure, 'responsibleName'>>) => ({
  ...(measure.id !== undefined ? { id: measure.id } : {}),
  ...(measure.dpiaRiskId !== undefined ? { dpia_risk_id: measure.dpiaRiskId } : {}),
  ...(measure.title !== undefined ? { title: measure.title.trim() } : {}),
  ...(measure.description !== undefined ? { description: measure.description.trim() } : {}),
  ...(measure.responsibleMembershipId !== undefined ? { responsible_membership_id: measure.responsibleMembershipId } : {}),
  ...(measure.status !== undefined ? { status: measure.status } : {}),
  ...(measure.dueDate !== undefined ? { due_date: measure.dueDate } : {}),
  ...(measure.sortOrder !== undefined ? { sort_order: measure.sortOrder } : {}),
});

export const parseRiskForBE = (risk: Partial<Omit<DpiaRisk, 'riskLevel' | 'measures'>>) => ({
  ...(risk.id !== undefined ? { id: risk.id } : {}),
  ...(risk.title !== undefined ? { title: risk.title.trim() } : {}),
  ...(risk.description !== undefined ? { description: risk.description.trim() } : {}),
  ...(risk.affectedRightsAndFreedoms !== undefined ? { affected_rights_and_freedoms: risk.affectedRightsAndFreedoms.trim() } : {}),
  ...(risk.likelihood !== undefined ? { likelihood: risk.likelihood } : {}),
  ...(risk.impact !== undefined ? { impact: risk.impact } : {}),
  ...(risk.sortOrder !== undefined ? { sort_order: risk.sortOrder } : {}),
});

export const parseUpdateDpiaForBE = (payload: UpdateDpiaInput) => ({
  revision: payload.revision,
  planned_processing_description: payload.plannedProcessingDescription.trim(),
  assessment_scope: payload.assessmentScope.trim(),
  project_or_change_context: payload.projectOrChangeContext.trim(),
  selected_purpose_ids: payload.selectedPurposeIds,
  necessity_assessment: payload.necessityAssessment.trim(),
  proportionality_assessment: payload.proportionalityAssessment.trim(),
  data_minimisation_assessment: payload.dataMinimisationAssessment.trim(),
  retention_and_access_assessment: payload.retentionAndAccessAssessment.trim(),
  overall_assessment: payload.overallAssessment.trim(),
  residual_risk_level: payload.residualRiskLevel,
  prior_consultation_assessment: payload.priorConsultationAssessment,
  owner_conclusion: payload.ownerConclusion.trim(),
  owner_response_comment: payload.ownerResponseComment.trim(),
});

export const parseSubmitDpiaForBE = (revision: number) => ({
  revision,
});

export const parseApproveDpiaForBE = (payload: { decision: DpiaDecision; reviewDecisionComment: string }) => ({
  decision: payload.decision,
  review_decision_comment: payload.reviewDecisionComment.trim(),
});

export const parseRequestDpiaClarificationForBE = (payload: { administratorClarificationComment: string }) => ({
  administrator_clarification_comment: payload.administratorClarificationComment.trim(),
});
